import { Component, Input, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { LookupOptionsData } from '../../jurisdiction.domain';
import { AllDropdownKeys, UrlSegments } from '../../jurisdiction.constants';
import { CommonService } from '../../../services/common.service';
import { AllLabels } from '../../../constants/app.constants';
import { FacilitiesFormService } from '../../facilities-form/facilities-form.service';
import { distinctUntilChanged } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { FiringSystemDataObject, FurnaceDataObject } from '../../facilities-form/facilities-form.domain';
import { CommunicationService } from '../../../services/communication.service';
import { ErrorService } from '../../../services/error/error.service';

@Component({
  selector: 'app-fireplace-ventilation-system',
  templateUrl: './fireplace-ventilation-system.component.html',
  styleUrls: ['./fireplace-ventilation-system.component.scss'],
})
export class FireplaceVentilationSystemComponent implements OnInit {
  @Input() firingSystemData: FiringSystemDataObject;
  @Input() furnaceData: FurnaceDataObject[];
  @Input() isReadOnly: boolean = false;

  firePlaceSystemFG: FormGroup;
  firePlaceData: Array<any>;
  fuelType: string;
  combustionBoiler: string;
  fuelTypeOptions: LookupOptionsData[];
  combustionBoilerOptions: LookupOptionsData[];
  firePlaceCheckboxLabels: Array<{ label: string; controlName: string }> = [];
  centiMeterSquare: string;
  isHeatValueGreaterThan35: boolean = false;
  formSubscription: Subscription;

  constructor(
    private commonService: CommonService,
    private formBuilder: FormBuilder,
    private facilitiesFormService: FacilitiesFormService,
    private communicationService: CommunicationService,
    private errorService: ErrorService
  ) {
    this.firePlaceSystemFG = this.formBuilder.group({
      heatSum: [''],
      setHeatSum: [''],
      arr1: this.formBuilder.array([]),
      arr2: this.formBuilder.array([]),
      furnace: [this.furnaceData],
    });
    this.firePlaceData = [
      { label1: 'producerType' },
      { label1: 'furnace' },
      { label1: 'proofOfUsability' },
      { label1: 'volume' },
      { label1: 'temp' },
      { label1: 'fuel' },
      { label1: 'heat' },
      { label1: 'setHeat' },
      { label1: '' },
    ];
    this.centiMeterSquare = AllLabels.units.centiMeterSquare;
  }

  get arr1() {
    return this.firePlaceSystemFG?.get('arr1') as FormArray;
  }

  get arr2() {
    return this.firePlaceSystemFG?.get('arr2') as FormArray;
  }

  ngOnInit(): void {
    if (this.furnaceData?.length) {
      for (let i = 0; i < this.furnaceData?.length; i++) {
        this.tableFormGroup(i);
      }
    } else {
      this.tableFormGroup();
    }
    this.furnaceInputDataLabels();
    this.addLabelFields();
    this.prepareCheckboxesFormControls();
    this.facilitiesFormService.registerForComponentFormUpdate.next({
      component: 'fireplaceVentilation',
      formData: this.firePlaceSystemFG,
    });
    this.formSubscription = this.firePlaceSystemFG.valueChanges
      .pipe(distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)))
      .subscribe(x => {
        this.updateFurnaceData();
        this.facilitiesFormService.registerForComponentFormUpdate.next({
          component: 'fireplaceVentilation',
          formData: this.firePlaceSystemFG,
        });
      });
    this.fuelType = AllDropdownKeys.FuelType;
    this.combustionBoiler = AllDropdownKeys.CombustionBoiler;
    this.fuelTypeOptions = this.commonService.getDropdownData(this.fuelType);
    this.combustionBoilerOptions = this.commonService.getDropdownData(this.combustionBoiler);
    this.furnaceInputData('heat');
    if (this.isHeatValueGreaterThan35 && !this.firingSystemData?.airSupplyOpenAirCheckbox) {
      this.openAirCheckBox(this.firingSystemData?.airSupplyOpenAirCheckbox, true);
      this.roomVentilationChange(true, true);
    }
    this.prefillOtherFuelTypeValue();
    this.facilitiesFormService.registerForActionOnFormSubmit.subscribe((value: boolean) => {
      if (value) {
        this.resetData();
      }
    });
  }

  openAirCheckBox(value, isInitial: boolean = false) {
    if (value || !this.isHeatValueGreaterThan35) {
      this.clearFormControlValidatorsAndValue('roomVentilation', true);
      this.firePlaceSystemFG.removeControl('roomVentilation');
      if (this.firePlaceSystemFG?.get('roomVentilationValueincm')) {
        this.clearFormControlValidatorsAndValue('roomVentilationValueincm', true);
        this.firePlaceSystemFG.removeControl('roomVentilationValueincm');
      }
    } else if (this.isHeatValueGreaterThan35) {
      this.firePlaceSystemFG.addControl('roomVentilation', new FormControl(''));
      this.setFormControlValidators('roomVentilation');
    }
    if (isInitial) {
      this.firePlaceSystemFG.get('roomVentilation')?.setValue(this.firingSystemData?.roomVentilation || '');
    }
  }

  updateFurnaceData() {
    const arr1Data = this.firePlaceSystemFG?.get('arr1')?.value;
    const arr2Data = this.firePlaceSystemFG?.get('arr2')?.value;
    const mergeFurnaceData = [];
    for (let i = 0; i < arr1Data?.length; i++) {
      mergeFurnaceData.push({
        ...arr1Data[i],
        ...arr2Data[i],
        systemId: this.furnaceData[i]?.systemId,
        furnaceId: this.furnaceData[i]?.furnaceId,
      });
    }
    this.firePlaceSystemFG.get('furnace').setValue(mergeFurnaceData);
  }

  furnaceInputData(label: string) {
    const array2 = this.firePlaceSystemFG.get('arr2')?.value;
    const isHeatValueGreaterthan35 = array2.some(x => +x.heat > 35);
    this.isHeatValueGreaterThan35 = isHeatValueGreaterthan35 || !!(label === 'heat' && isHeatValueGreaterthan35);
    if (!this.firePlaceSystemFG.get('airSupplyOpenAirCheckbox')?.value) {
      if (!this.isHeatValueGreaterThan35 && label === 'heat') {
        this.openAirCheckBox(false, false);
      }
      this.firePlaceSystemFG.addControl('isHeatValueGreaterthan35', new FormControl(this.isHeatValueGreaterThan35));
      if (this.isHeatValueGreaterThan35) {
        this.openAirCheckBox(false, false);
      } else {
        if (label === 'heat') {
          this.openAirCheckBox(false, false);
        }
      }
    }
    if (label !== 'temp') {
      const isFieldEmpty = array2.every(x => !x[label]);
      const sumValue = array2.reduce((sum, furnace) => sum + +furnace[label], 0);
      if (sumValue >= 0) {
        this.firePlaceSystemFG.get(`${label}Sum`).setValue(sumValue);
      }
      if (isFieldEmpty) {
        this.firePlaceSystemFG.get(`${label}Sum`).setValue('');
      }
    }
  }

  furnaceInputDataLabels() {
    const sumArr = ['heat', 'setHeat'];
    for (const label of sumArr) {
      this.furnaceInputData(label);
    }
  }

  roomVentilationChange(value: any, isInitial: boolean = false) {
    if (value) {
      this.firePlaceSystemFG.addControl('roomVentilationValueincm', new FormControl(this.firingSystemData?.roomVentilationValueincm || ''));
      this.setFormControlValidators('roomVentilationValueincm');
    }
    if (!isInitial) {
      this.clearFormControlValidatorsAndValue('roomVentilationValueincm', false);
    }
  }

  setFormControlValidators(formControlName: string) {
    if (formControlName) {
      this.firePlaceSystemFG.get(formControlName).setValidators(Validators.required);
      this.firePlaceSystemFG.get(formControlName).markAsTouched();
      this.firePlaceSystemFG.get(formControlName).updateValueAndValidity();
    }
  }

  clearFormControlValidatorsAndValue(formControlName: string, isClearValidators?: boolean) {
    if (formControlName) {
      this.firePlaceSystemFG.get(formControlName)?.patchValue('');
    }
    if (isClearValidators) {
      this.firePlaceSystemFG.get(formControlName)?.clearValidators();
    }
    this.firePlaceSystemFG.get(formControlName)?.updateValueAndValidity();
  }

  tableFormGroup(index?: number) {
    const data1 = {};
    const data2 = {};
    this.firePlaceData.forEach((x, j) => {
      const value = index > -1 ? this.furnaceData && this.furnaceData[index][x.label1] : '';
      if (x.label1 && j < 4) {
        data1[x.label1] = new FormControl(value);
      } else if (x.label1) {
        data2[x.label1] = new FormControl(value);
      }
    });

    const newItem1 = this.formBuilder.group({
      ...data1,
    });
    const newItem2 = this.formBuilder.group({
      ...data2,
    });
    this.arr1?.push(newItem1);
    this.arr2?.push(newItem2);
  }

  addLabelFields() {
    const labelsControlNames = [
      'withBlower',
      'withFlowProtection',
      'airSupplyInstallationRoom',
      'emissionGas',
      'withoutBlower',
      'withoutFlowProtection',
      'airSupplyOpenAir',
    ];
    for (let i = 0; i <= labelsControlNames.length - 1; i++) {
      const data = { label: 'label-' + (i + 1), controlName: labelsControlNames[i] + 'Checkbox' };
      this.firePlaceCheckboxLabels.push(data);
    }
  }

  prepareCheckboxesFormControls() {
    this.firePlaceCheckboxLabels.forEach(labelsData => {
      this.firePlaceSystemFG.addControl(labelsData.controlName, new FormControl(this.firingSystemData[labelsData.controlName] || ''));
    });
  }

  addCell() {
    this.tableFormGroup();
  }

  deleteCell(ind: number) {
    const furnaceId = this.furnaceData[ind]?.furnaceId;
    const heat = +this.arr2.value[ind].heat;
    const setHeat = +this.arr2.value[ind].setHeat;
    if (furnaceId) {
      this.communicationService.deleteData(`${UrlSegments.UpdateGasFurnace}=${furnaceId}`).subscribe(
        response => {
          this.errorService.throwErrorIfFailure(response, true);
          this.arr1.removeAt(ind);
          this.arr2.removeAt(ind);
          this.furnaceData.splice(ind, 1);
          this.updateFurnaceData();
        },
        error => {
          this.errorService.throwErrorIfFailure(error, true);
        }
      );
    } else {
      this.arr1.removeAt(ind);
      this.arr2.removeAt(ind);
    }
    const heatCurrentValue = this.firePlaceSystemFG.get('heatSum').value;
    const setHeatCurrentValue = this.firePlaceSystemFG.get('setHeatSum').value;
    this.firePlaceSystemFG.get('heatSum').setValue(heatCurrentValue - heat ? heatCurrentValue - heat : '');
    this.firePlaceSystemFG.get('setHeatSum').setValue(setHeatCurrentValue - setHeat ? setHeatCurrentValue - setHeat : '');
    if (heatCurrentValue - heat < 35) {
      this.isHeatValueGreaterThan35 = false;
      this.openAirCheckBox(true, false);
    }
  }

  selectDropdownValue(value, ind) {
    if (value === 'other') {
      (this.arr2?.controls[ind] as FormGroup).addControl(
        'otherFuelType',
        new FormControl(this.furnaceData[ind]?.otherFuelType || '', [Validators.required])
      );
      (this.arr2?.controls[ind] as FormGroup).controls.otherFuelType.markAsTouched();
    } else {
      (this.arr2?.controls[ind] as FormGroup).removeControl('otherFuelType');
    }
  }

  prefillOtherFuelTypeValue() {
    this.furnaceData?.forEach((data, ind) => {
      this.selectDropdownValue(data.fuel, ind);
    });
  }

  getTableLabel(label: string) {
    return `facilities-data.form-fields.${label}`;
  }

  getCheckBoxLabel(label: string) {
    return `facilities-data.form-fields.fireplace-system-labels.${label}`;
  }

  private resetData(): void {
    this.formSubscription.unsubscribe();
  }
}
